import { AxiosResponse } from "axios";
import { Action } from "redux-saga";
import { call, CallEffect, PutEffect, select } from "redux-saga/effects";
import createAccount from "../account/create";
import { clearString, getFirstAndLastName, getFormattedCart, getFormattedDeviceFingerPrintID } from "../../../utils/functions";
import api from "../../../settings/api";
import apiPCI from "../../../settings/apiPCI";
import environment from "../../../settings/environment";
import { ReducersTypes } from "../../reducers/Models";


export interface Fetch {
    plate: string;
    card: {
        number: string;
        name: string;
        expiration: string;
        document: string;
    }
    address: {
        zipcode: string;
        streetName: string;
        neighborhood: string;
        addressStreet: string;
        number: string;
        complement: string;
        city: string;
        state: string;
        stateCode: string;
    };
    visitorId: string;
    callback: (success: boolean, data?: { errorMessage?: string; sqPagamento?: string }) => void;
}

export interface PaymentCardAction {
    type: string;
    payload: Fetch;
}

export default function* paymentCard({
    payload,
}: PaymentCardAction): Generator<
    CallEffect<unknown> | PutEffect<Action>,
    void,
    AxiosResponse
> {
    try {

        const deviceFingerprintID = getFormattedDeviceFingerPrintID(payload.visitorId);

        const document = payload?.card?.document;
        const name =  (payload as any)?.name;
        const email = (payload as any)?.email;
        const phone = (payload as any)?.phone;
        const licensePlate = clearString(payload?.plate.toLocaleUpperCase());

        // @ts-expect-error
        const user: ReducersTypes['userReducer'] = yield select((state: ReducersTypes) => state.userReducer);
        const nrc = user?.nrc;
        const accountId = user?.accountId;

        // Se tiver nrc e accountId (fluxo zapay), usa diretamente sem criar conta
        let account: { customerId: string; accountId: string } | unknown;
        
        if (nrc && accountId) {
            account = {
                customerId: nrc,
                accountId: accountId
            };
        } else {
            account = yield call(createAccount, {
                type: "",
                payload: {
                    licensePlate,
                    document: document,
                    name: name,
                    email: email,
                    phone: phone,
                }
            });

            if (!account) throw new Error();
        }

        const uriPCI = "/api/v1/tokenizing";
        const responsePCI = yield call(apiPCI.post, uriPCI, {
            dataToToken: clearString(payload?.card?.number),
        });

        const uriBrands = "/v1/financeiro/bandeiras";
        const responseBrands = yield call(api.get, uriBrands);

        const {
            total,
            selected,
        } = getFormattedCart({
            // @ts-expect-error sagas types
            debts: payload?.debts,
            // @ts-expect-error sagas types
            cart: payload?.cart,
        });

        const brand = responseBrands?.data?.listaBandeiraCartao?.find((item: {
            descricaoBandeira: string;
        }) => item?.descricaoBandeira === responsePCI?.data?.additionalInformation?.brand?.name);

        const { firstName, lastName } = getFirstAndLastName(payload?.card?.name);

        const responseValidate = yield call(api.post, `/v1/payments/credit-card/validate`, {
            creditCardData: {
                buyerInformation: {
                    document: clearString(payload?.card?.document),
                    // @ts-expect-error sagas types
                    nrc: account?.customerId
                },
                ...(environment.api.prefix !== "qa" ? {
                    deviceInformation: {
                        fingerprintSessionId: deviceFingerprintID,
                    },
                } : null),
                orderInformation: {
                    billTo: {
                        country: "BR",
                        firstName: firstName,
                        lastName: lastName,
                    },
                    totalAmount: Number(Number(total)?.toFixed(2))
                },
                paymentInformation: {
                    card: {
                        cardIdentifier: brand?.codigoBandeira,
                        expirationMonth: payload?.card?.expiration?.split('/')?.[0],
                        expirationYear: payload?.card?.expiration?.split('/')?.[1],
                        number: responsePCI?.data?.token
                    }
                }
            },
        });

        const uriMethods = `/v1/payments/methods`;
        const responseMethods = yield call(api.get, uriMethods);

        const method = responseMethods?.data?.data?.find((item: {
            method: string
        }) => item.method === "credit-card");

        const body = {
            document: clearString(payload?.card?.document),
            // @ts-expect-error sagas types
            requestId: payload?.requestId,
            // @ts-expect-error sagas types
            customerIdentification: account?.customerId,
            // @ts-expect-error sagas types
            accountIdentification: account?.accountId,
            paymentValue: Number(Number(total)?.toFixed(2)),
            licensePlate: clearString(payload.plate.toLocaleUpperCase()),
            emissionChannel: method?.code,
            tollPassages: selected,
            email: email,
            phoneNumber: clearString(phone),
            creditCardNumber: responsePCI?.data?.token,
            creditCardOwner: payload?.card?.name,
            cardIdentifier: brand?.codigoBandeira,
            monthDueDate: Number(payload?.card?.expiration?.split('/')?.[0]),
            yearDueDate: Number(payload?.card?.expiration?.split('/')?.[1]),
            creditCardValidationToken: responseValidate?.data?.data?.validationToken,
            ...(environment.api.prefix !== "qa" ? {
                deviceInformation: {
                    fingerprintSessionId: deviceFingerprintID
                }
            } : null)
        };

        const response = yield call(api.post, `/v1/payments/credit-card`, body);

        if (response?.data?.idTransaction) {
            yield call(payload.callback, true, { sqPagamento: response?.data?.sqPagamento });
        } else {
            yield call(payload.callback, false, { errorMessage: "error" });
        }

    } catch (error) {
        yield call(payload.callback, false, { errorMessage: "timeout" });
    }
}